import React, { useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Autocomplete,
  Backdrop,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Typography,
  TextField
} from '@mui/material';
import { useCreate, useInvalidate, useMany, useModal, useResourceParams, useUpdate } from '@refinedev/core';
import { useAutocomplete, useDataGrid } from '@refinedev/mui';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';

import { ATTACKIQ_ID } from '@attackiq/constants';

import { Module, ModuleContent, ProjectTemplateTestScenario, Scenario } from '../../../../../../../../src/types';
import SavesOnTheFlyAlert from '../../../../../../../components/SavesOnTheFlyAlert';
import useDetailedProjectTemplateTest from '../../../../../../../hooks/useDetailedProjectTemplateTest';

import ScenarioDraggableItem from './ScenarioDraggableItem';

const IN_DEVELOPMENT_MODULE_DISPLAY_NAME = 'In Development';

interface IProps {
  testName: string;
  testId: string;
}

const ManageTestScenarios: React.FC<IProps> = ({ testName, testId }) => {
  const [_projectTemplateTestScenarios, _setProjectTemplateTestScenarios] = useState<ProjectTemplateTestScenario[]>([]);
  const [mutationInProgress, setMutationInProgress] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);

  const { visible, show, close } = useModal();
  const { id: project_template_id } = useResourceParams();

  const { data: projectTemplateTest, refetch } = useDetailedProjectTemplateTest(testId);

  const { dataGridProps } = useDataGrid<ModuleContent>({
    resource: 'module_contents',
    filters: {
      permanent: [
        {
          field: 'content_id',
          operator: 'eq',
          value: project_template_id
        }
      ]
    },
    pagination: {
      mode: 'off'
    },
    queryOptions: {
      enabled: visible
    }
  });

  const templateModuleIds = dataGridProps.rows.map((item: ModuleContent) => item.module_id);

  const {
    result: modulesData,
    query: { isLoading: isLoadingModules }
  } = useMany<Module>({
    resource: 'modules',
    ids: [...new Set(templateModuleIds)],
    queryOptions: {
      enabled: visible && templateModuleIds.length > 0
    }
  });

  const templateModuleDisplayNames = modulesData?.data?.map(module => module.display_name) || [];

  const [prevProjectTemplateTest, setPrevProjectTemplateTest] = useState(projectTemplateTest);

  if (prevProjectTemplateTest !== projectTemplateTest) {
    setPrevProjectTemplateTest(projectTemplateTest);

    if (projectTemplateTest && projectTemplateTest.project_template_test_scenarios) {
      _setProjectTemplateTestScenarios(projectTemplateTest.project_template_test_scenarios);
    }
  }

  const [, drop] = useDrop({
    accept: 'test_scenario'
  });

  const { mutateAsync: updateMutateAsync } = useUpdate();
  const { mutateAsync: createMutateAsync } = useCreate();
  const invalidate = useInvalidate();

  const mutateScenarioOrder = async () => {
    setMutationInProgress(true);
    for (const [index, projectTemplateTestScenario] of _projectTemplateTestScenarios.entries()) {
      await updateMutateAsync({
        resource: 'assessment_template_test_scenarios',
        id: projectTemplateTestScenario.id,
        values: {
          order: index + 1
        },
        invalidates: []
      });
    }

    await refetch();
    setMutationInProgress(false);
  };

  const performAddScenario = async (scenarioId: string) => {
    setMutationInProgress(true);
    await createMutateAsync({
      resource: 'assessment_template_test_scenarios',
      values: {
        company: ATTACKIQ_ID,
        order: _projectTemplateTestScenarios.length + 1,
        project_template: project_template_id,
        project_template_test: testId,
        scenario: scenarioId
      }
    });

    await refetch();
    setMutationInProgress(false);
  };

  const addNewScenario = async (scenario: Scenario) => {
    const scenarioHasInDevelopmentModule = scenario.modules.includes(IN_DEVELOPMENT_MODULE_DISPLAY_NAME);
    const templateHasInDevelopmentModule = templateModuleDisplayNames.includes(IN_DEVELOPMENT_MODULE_DISPLAY_NAME);
    const shouldWarn = scenarioHasInDevelopmentModule && !templateHasInDevelopmentModule;

    if (shouldWarn) {
      setSelectedScenario(scenario);
      setShowWarning(true);
    } else {
      await performAddScenario(scenario.id);
    }
  };

  const { autocompleteProps } = useAutocomplete<Scenario>({
    resource: 'scenarios',
    pagination: {
      pageSize: 20
    },
    debounce: 400,
    onSearch: searchText => [
      {
        field: 'search',
        operator: 'eq',
        value: searchText
      }
    ]
  });

  const findTestScenario = (id: string) => {
    const projectTemplateTestScenario = _projectTemplateTestScenarios.find(
      (ptts: ProjectTemplateTestScenario) => ptts.id === id
    );

    return {
      projectTemplateTestScenario,
      index: _projectTemplateTestScenarios.indexOf(projectTemplateTestScenario as ProjectTemplateTestScenario)
    };
  };

  const moveTestScenario = (id: string, atIndex: number) => {
    const { projectTemplateTestScenario, index } = findTestScenario(id);
    if (!projectTemplateTestScenario) return;

    _setProjectTemplateTestScenarios(
      update(_projectTemplateTestScenarios, {
        $splice: [
          [index, 1],
          [atIndex, 0, projectTemplateTestScenario]
        ]
      })
    );
  };

  const closeAndRevalidateTests = () => {
    invalidate({
      resource: 'assessment_template_tests',
      invalidates: ['all']
    });
    close();
  };

  const handleWarningConfirm = async () => {
    setShowWarning(false);
    if (selectedScenario) {
      await performAddScenario(selectedScenario.id);
    }
    setSelectedScenario(null);
  };

  const handleWarningCancel = () => {
    setShowWarning(false);
    setSelectedScenario(null);
  };

  return (
    <>
      <Button onClick={show}>Manage Scenarios</Button>
      <Modal open={visible} onClose={closeAndRevalidateTests}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            minHeight: '40%',
            maxHeight: '80%',
            bgcolor: 'background.paper',
            p: 3,
            overflow: 'auto'
          }}
        >
          <Backdrop open={mutationInProgress}>
            <CircularProgress />
          </Backdrop>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="h4" component="h1">
              Manage scenarios for "{testName}"
            </Typography>
            <IconButton onClick={closeAndRevalidateTests}>
              <Close />
            </IconButton>
          </Box>

          <Typography variant="overline">Add, remove or reorder scenarios</Typography>
          <SavesOnTheFlyAlert />

          {_projectTemplateTestScenarios?.length === 0 && (
            <Typography variant="body1" component="h1">
              No scenarios yet, add one below
            </Typography>
          )}

          <ol ref={drop}>
            {_projectTemplateTestScenarios.map((ptts: ProjectTemplateTestScenario) => (
              <ScenarioDraggableItem
                key={ptts.id}
                id={ptts.id}
                mutateTestScenario={mutateScenarioOrder}
                testId={testId}
                findTestScenario={findTestScenario}
                moveTestScenario={moveTestScenario}
                testScenario={ptts}
              />
            ))}
          </ol>
          <Box>
            <Autocomplete
              {...autocompleteProps}
              disabled={isLoadingModules}
              getOptionLabel={(item: Scenario) => item.name}
              filterOptions={options => {
                const alreadySelectedScenariosIds = _projectTemplateTestScenarios.map(
                  (ptts: ProjectTemplateTestScenario) => ptts.scenario.id
                );
                return options.filter((option: Scenario) => !alreadySelectedScenariosIds.includes(option.id));
              }}
              renderInput={params => (
                <TextField {...params} label="Search scenarios" margin="normal" variant="outlined" />
              )}
              onChange={(_, scenario) => {
                if (scenario) {
                  addNewScenario(scenario);
                }
              }}
            />
          </Box>
        </Box>
      </Modal>

      <Dialog open={showWarning} onClose={handleWarningCancel} fullWidth>
        <DialogTitle>Warning</DialogTitle>
        <DialogContent>
          This scenario is part of the In Development module, but the assessment template does not include it. Please,
          confirm with the content team if you are not sure about this action.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningCancel}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={handleWarningConfirm} disabled={mutationInProgress}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default ManageTestScenarios;
